import React, { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    console.log('ErrorBoundary caught error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Simple error page without external dependencies
      return (
        <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-[#6C5CE7] to-[#8B7FE8] flex items-center justify-center">
          {/* Main content */}
          <div className="relative z-10 text-center px-4 max-w-2xl">
            {/* Error icon */}
            <div className="mb-8 flex justify-center">
              <div className="flex items-center justify-center">
                <div className="relative">
                  <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
                    <svg
                      className="w-16 h-16 md:w-20 md:h-20 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="absolute top-0 right-0 w-3 h-3 bg-white/50 rounded-full animate-ping"></div>
                  <div className="absolute bottom-0 left-0 w-2 h-2 bg-white/40 rounded-full animate-ping" style={{animationDelay: '200ms'}}></div>
                  <div className="absolute top-1/2 -right-4 w-2 h-2 bg-white/30 rounded-full animate-ping" style={{animationDelay: '400ms'}}></div>
                </div>
              </div>
            </div>

            {/* Error message */}
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Oops! Something went wrong
            </h2>
            <p className="text-white/80 text-base md:text-lg mb-8">
              We encountered an unexpected error. Don't worry, our team has been notified and we're working on it.
            </p>

            {/* Action button */}
            <button
              onClick={this.handleReset}
              className="bg-white text-[#6C5CE7] px-8 py-3 rounded-lg font-semibold text-lg hover:scale-105 transition-transform duration-200 shadow-lg hover:shadow-xl mr-4"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-[#6C5CE7] text-white px-8 py-3 rounded-lg font-semibold text-lg hover:scale-105 transition-transform duration-200 shadow-lg hover:shadow-xl border-2 border-white/20"
            >
              Back to Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based error handler for functional components
 */
export function useErrorHandler() {
  return (error: Error, errorInfo?: React.ErrorInfo) => {
    console.error('Error caught by error handler:', error, errorInfo);
    // You can integrate with error reporting services here
    // Example: Sentry.captureException(error);
  };
}